import { Star, User } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { Progress, Text, View, XStack, YStack } from "tamagui";
import { useStores } from "../../../core/stores";
import { InfoRowItem } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import type { RatingManager } from "./RatingManager";

export const RatingUsers: React.FC<{
  // biome-ignore lint/suspicious/noExplicitAny: Because
  ratingsManager: RatingManager<any, any>;
  dataUid: string;
  emptyText?: string;
}> = observer(({ ratingsManager, dataUid }) => {
  const { usersStore } = useStores();
  const currentUserId = ratingsManager.module.plan.userInPlan?.uid;

  const ratings = ratingsManager.getRatings(dataUid).map((rating) => ({
    userId: rating.data.userId,
    user: usersStore.byId.get(rating.data.userId),
    rating: rating.data.rating,
  }));

  if (!ratings.length) {
    return <Text fontStyle="italic">No ratings</Text>;
  }

  return (
    <Text>
      Rated by{" "}
      {ratings.map((rating, index) => {
        const displayName = rating.user?.data.displayName;
        const formattedDisplayName =
          currentUserId === rating.user?.uid ? (
            <strong>{displayName}</strong>
          ) : (
            displayName
          );

        return (
          <React.Fragment key={rating.userId}>
            {index !== 0 ? ", " : ""}
            {formattedDisplayName} ({rating.rating})
          </React.Fragment>
        );
      })}
    </Text>
  );
});

export const RatingsInput: React.FC<{
  dataUid: string;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  ratings: RatingManager<any, any>;
  size?: number;
  disabled?: boolean;
  withAverage?: boolean;
  showHint?: boolean;
}> = observer(
  ({ dataUid, ratings, withAverage, disabled, size = 42, showHint }) => {
    const averageRating = ratings.getAverage(dataUid);
    const userRating = ratings.getUserRating(dataUid)?.data.rating;

    return (
      <YStack gap="$large">
        <XStack gap="$small" justifyContent="center" alignItems="center">
          {[1, 2, 3, 4, 5].map((rating) => {
            const isActive = rating <= Math.round(averageRating);
            const isUserRating = userRating === rating;
            return (
              <Pressable
                disabled={disabled}
                key={rating}
                onPress={() => ratings.rateDataItem(dataUid, rating)}
              >
                <Star
                  size={size}
                  strokeOpacity={0.5}
                  color={"#000"}
                  strokeWidth={!disabled && isUserRating ? 1 : 0}
                  fill={isActive ? "#ffd84e" : "#ccc"}
                />
              </Pressable>
            );
          })}
          {withAverage ? (
            <Label fontSize={size} lineHeight={size}>
              {averageRating.toFixed(1)}
            </Label>
          ) : undefined}
        </XStack>
        {!userRating && showHint && (
          <View alignSelf="center">
            <TipsText text="Add your vote by tapping on a star" />
          </View>
        )}
      </YStack>
    );
  },
);

export const AdvancedRatingsInput: React.FC<{
  dataUid: string;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  ratings: RatingManager<any, any>;
}> = observer(({ dataUid, ratings: ratingManager }) => {
  const averageRating = ratingManager.getAverage(dataUid);
  const totalNumRatings = ratingManager.getRatings(dataUid).length;
  const { usersStore } = useStores();

  return (
    <XStack gap="$larger">
      <View
        width={64}
        height={64}
        backgroundColor={"$background"}
        borderRadius={32}
        alignItems="center"
        justifyContent="center"
      >
        <Title flexShrink={0}>{averageRating.toFixed(1)}</Title>
      </View>
      <YStack gap="$medium" flex={1}>
        {[5, 4, 3, 2, 1].map((rating) => {
          const ratings = ratingManager.getRatings(dataUid, rating);
          const numRatings = ratings.length;

          return (
            <YStack key={rating} gap="$small">
              <XStack gap="$small" alignItems="center">
                <View
                  flexShrink={0}
                  width={16}
                  height={16}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Label>{rating}</Label>
                </View>
                <Star size={16} fill={"#ffd84e"} color={"#ffd84e"} />
                <Progress value={(numRatings / totalNumRatings) * 100} flex={1}>
                  <Progress.Indicator
                    backgroundColor={"$primary"}
                    animation="bouncy"
                  />
                </Progress>
              </XStack>
              {ratings.length ? (
                <InfoRowItem
                  item={{
                    text: ratings
                      .map(
                        (ratingItem) =>
                          usersStore.byId.get(ratingItem.data.userId)?.data
                            .displayName,
                      )
                      .join(", "),
                    icon: User,
                  }}
                />
              ) : undefined}
            </YStack>
          );
        })}
      </YStack>
    </XStack>
  );
});
